import { useState, useEffect } from 'react'
import { createClient, type User, type SupabaseClient } from '@supabase/supabase-js'
import { API_BASE, SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/config'

// ── Supabase singleton ────────────────────────────────────────
let _supabase: SupabaseClient | null = null

export function getSupabase(): SupabaseClient {
  if (!_supabase) {
    _supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        detectSessionInUrl: true,
        // Do NOT use flowType:'pkce' — it breaks email/password login
      },
    })
  }
  return _supabase
}

export interface AuthUser {
  id: string
  email: string
  name?: string
  avatarUrl?: string
  role?: string
  isAdmin?: boolean
  referralCode?: string | null
  credits?: number
  onboardingCompleted?: boolean
  customInstructions?: string
}

function mapUser(u: User): AuthUser {
  const metadata = u.user_metadata ?? {}
  return {
    id: u.id,
    email: u.email || '',
    name: metadata.full_name || metadata.name || u.email?.split('@')[0],
    avatarUrl: metadata.avatar_url,
    role: 'user',
    isAdmin: false,
    onboardingCompleted: metadata.onboarding_completed === true || metadata.onboardingCompleted === true,
    customInstructions: metadata.custom_instructions || metadata.customInstructions || '',
  }
}

// ── Read Supabase's own persisted session from localStorage synchronously ──
// Supabase stores the session under the key: sb-<project-ref>-auth-token
// Derive the project ref from SUPABASE_URL to avoid hardcoding.
const _supabaseProjectRef = SUPABASE_URL
  ? new URL(SUPABASE_URL).hostname.split('.')[0]
  : 'cauaqoqvpvjpeghejgvj'  // fallback for local dev without env vars
const SUPABASE_SESSION_KEY = `sb-${_supabaseProjectRef}-auth-token`

function readSessionUserSync(): AuthUser | null {
  try {
    const raw = localStorage.getItem(SUPABASE_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Supabase stores { access_token, refresh_token, user, expires_at, ... }
    // It may also be nested under a 'currentSession' key in some versions
    const session = parsed?.currentSession ?? parsed
    const u: User | undefined = session?.user
    if (!u?.id) return null
    // Check token hasn't expired (expires_at is Unix seconds)
    const expiresAt: number | undefined = session?.expires_at
    if (expiresAt && expiresAt * 1000 < Date.now()) {
      // Token expired — but Supabase will refresh it via onAuthStateChange
      // Still return the user so we don't flash a spinner
      return mapUser(u)
    }
    return mapUser(u)
  } catch {
    return null
  }
}

async function fetchUserRole(supabaseUser: User): Promise<AuthUser> {
  const base = mapUser(supabaseUser)
  try {
    const sb = getSupabase()
    // Sync user to profiles table (upsert) so they appear in admin panel
    // Only set avatar_url if user_metadata has one (don't overwrite uploaded avatar with empty string)
    const upsertData: Record<string, string> = {
      id: supabaseUser.id,
      email: supabaseUser.email || '',
    }
    const metadataName = supabaseUser.user_metadata?.full_name || supabaseUser.user_metadata?.name
    if (metadataName) {
      upsertData.display_name = metadataName
    }
    if (supabaseUser.user_metadata?.avatar_url) {
      upsertData.avatar_url = supabaseUser.user_metadata.avatar_url
    }
    await sb.from('profiles').upsert(upsertData, { onConflict: 'id', ignoreDuplicates: false })

    const { data: { session } } = await sb.auth.getSession()
    if (!session?.access_token) {
      return base
    }
    const response = await fetch(`${API_BASE}/api/profile/bootstrap`, {
      headers: {
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
    })
    if (response.status === 401) {
      return base
    }
    if (response.ok) {
      const data = await response.json()
      const profile = data?.profile ?? null
      return {
        ...base,
        name: profile?.display_name || base.name,
        role: profile?.role ?? 'user',
        isAdmin: profile?.role === 'admin',
        avatarUrl: profile?.avatar_url || base.avatarUrl,
        referralCode: profile?.referral_code ?? null,
        credits: profile?.credits ?? 0,
        onboardingCompleted: profile?.onboarding_completed === true || base.onboardingCompleted === true,
      }
    }
  } catch {
    // Fall back to base user when bootstrap is unavailable.
  }
  return base
}

export function useAuth() {
  // Synchronous init: read Supabase's own localStorage session.
  // This is INSTANT — no network, no spinner, no waiting.
  const [user, setUser] = useState<AuthUser | null>(() => readSessionUserSync())

  // loading is only true when there is NO cached session at all.
  // For returning users, loading starts as false → no spinner ever shown.
  const [loading, setLoading] = useState<boolean>(() => readSessionUserSync() === null)

  useEffect(() => {
    // Guard: if Supabase config is missing, stop loading and show landing/login
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
      setUser(null)
      setLoading(false)
      return
    }

    const supabase = getSupabase()
    let resolved = false

    const resolve = (authUser: AuthUser | null) => {
      resolved = true
      setUser(authUser)
      setLoading(false)
    }

    void (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          setUser(mapUser(session.user))
          setLoading(false)
          const enriched = await fetchUserRole(session.user)
          setUser(enriched)
          resolved = true
        }
      } catch {
        // Fall back to auth state listener below
      }
    })()

    // onAuthStateChange is the single source of truth.
    // It fires for: INITIAL_SESSION, SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        // Immediately set user from session (no delay)
        setUser(mapUser(session.user))
        setLoading(false)
        // Enrich with role in background — don't block rendering
        fetchUserRole(session.user).then(enriched => {
          setUser(enriched)
        }).catch(() => {
          // ignore role fetch error
        })
        resolved = true
      } else {
        // SIGNED_OUT or no session
        resolve(null)
      }
    })

    // Hard safety net: if onAuthStateChange never fires within 4s,
    // stop loading and show the login page.
    const safetyTimeout = setTimeout(() => {
      if (!resolved) {
        setLoading(false)
      }
    }, 4000)

    return () => {
      clearTimeout(safetyTimeout)
      subscription.unsubscribe()
    }
  }, [])

  const signOut = async () => {
    // Clear all sine_ prefixed keys from localStorage,
    // but KEEP sine_onboarding_done_* so onboarding is not shown again after re-login.
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sine_') && !key.startsWith('sine_onboarding_done_')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k))

    // Also clear the Supabase session key to prevent stale cache reads
    localStorage.removeItem(SUPABASE_SESSION_KEY)

    try {
      if (SUPABASE_URL && SUPABASE_ANON_KEY) {
        await getSupabase().auth.signOut()
      }
    } catch { /* ignore */ }

    setUser(null)
    window.location.href = '/'
  }

  const refreshUser = async () => {
    try {
      if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return
      const supabase = getSupabase()
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        const enriched = await fetchUserRole(session.user)
        setUser(enriched)
      }
    } catch {
      // ignore
    }
  }

  return { user, loading, signOut, refreshUser }
}
